import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Brain, BarChart3, BookOpen, Film, Users, ArrowRight, CheckCircle2 } from 'lucide-react';

const features = [
  {
    slug: 'ai-coach',
    icon: Brain,
    title: 'AI Video Coach',
    tagline: 'Get instant technique feedback from AI',
    description: 'Upload your match or practice footage and our AI Coach — powered by Claude Sonnet 4.5 — analyzes 150 frames focusing on the nearest player. Receive detailed feedback on footwork, racket position, body posture, shuttle contact, and reaction time, plus personalized drills.',
    benefits: [
      'Analyzes 150 frames per video for thorough assessment',
      'Focuses on the nearest player — no confusion in doubles',
      'Rates 6 technique areas: footwork, contact, racket, eyes, posture, reaction',
      'Generates personalized drills targeting your weaknesses',
      'Supports videos up to 1 hour long',
      'Background processing — continue using the app while it works'
    ],
    color: 'from-indigo-600 to-purple-600'
  },
  {
    slug: 'match-tracker',
    icon: BarChart3,
    title: 'Match Tracker & Analytics',
    tagline: 'Log every game. See every pattern.',
    description: 'Track singles and doubles matches with full score details, best-of-three sets, opponent names, tournament info, and notes. View your stats as interactive charts — win/loss ratio, performance trends, cumulative wins, win streaks, and head-to-head records against specific opponents.',
    benefits: [
      'Log singles, doubles, practice, and tournament matches',
      'Best-of-three set support with game-by-game scores',
      'Win/loss pie chart, performance trend line, and cumulative bar chart',
      'Filter match history by opponent, date range, or type',
      'Share match highlights as social posts',
      'Track win streaks and age category rankings'
    ],
    color: 'from-blue-600 to-cyan-600'
  },
  {
    slug: 'training-diary',
    icon: BookOpen,
    title: 'Training Diary',
    tagline: 'Remember what you learned. Track your growth.',
    description: 'After every session — practice, match, drill, or fitness — log structured notes about what went well, what needs work, and your goals for next time. Filter by session type and review your progression over weeks and months.',
    benefits: [
      '5 session types: Practice, Match, Drill, Fitness, Video Review',
      'Structured fields: what went well, what to improve, goals for next session',
      'Date-sorted timeline of your training journey',
      'Filter by session type to spot patterns',
      'Additional free-form notes for any extra thoughts',
      'Full edit and delete — keep your diary clean'
    ],
    color: 'from-emerald-600 to-teal-600'
  },
  {
    slug: 'social-shorts',
    icon: Film,
    title: 'Social Shorts & Highlights',
    tagline: 'Share your best rallies with the community.',
    description: 'Post 60-second match highlights, rally clips, and training moments. Like, comment, and tag friends. Build a feed of your best badminton content and discover what other players are sharing.',
    benefits: [
      '60-second maximum for true short-form content',
      'Like, comment, and tag other players',
      'Share match highlights directly from match history',
      'Video editor with filters, text overlays, and music',
      'Community feed to discover other players\' content',
      'Works as a badminton-focused social network'
    ],
    color: 'from-pink-600 to-rose-600'
  },
  {
    slug: 'friends-leaderboard',
    icon: Users,
    title: 'Friends & Leaderboard',
    tagline: 'Find players. Compare stats. Compete.',
    description: 'Search for other players by username, get smart friend recommendations based on mutual connections and activity, and view anyone\'s public stats. See how you rank against the community on the global leaderboard.',
    benefits: [
      'Live search with instant results as you type',
      'Smart recommendations based on mutual friends',
      'View any player\'s public stats, win rate, and top opponents',
      'Global leaderboard ranked by win rate',
      'Friend request system with accept/reject',
      'Direct messaging with friends'
    ],
    color: 'from-amber-600 to-orange-600'
  }
];

const FeaturesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="features-page">
      {/* Nav */}
      <nav className="w-full bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_blog-56/artifacts/ar7lcvoq_WhatsApp%20Image%202026-02-21%20at%2022.08.34.jpeg" alt="Badminton Stats" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold text-blue-600">Badminton Stats</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/features" className="text-sm font-medium text-blue-600 hidden sm:inline">Features</Link>
            <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-700 hidden sm:inline">Privacy</Link>
            <Button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="features-signup-btn">
              Get Started Free
            </Button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-3">
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <ol className="flex items-center gap-1">
            <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
            <li>/</li>
            <li className="text-gray-900 font-medium">Features</li>
          </ol>
        </nav>
      </div>

      {/* Hero */}
      <header className="max-w-4xl mx-auto px-6 pt-8 pb-16 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
          Every Tool a Badminton Player Needs
        </h1>
        <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
          From AI-powered video coaching to structured training diaries — Badminton Stats gives you a complete system to track, analyze, and improve your game.
        </p>
      </header>

      {/* Feature Cards */}
      <main className="max-w-7xl mx-auto px-6 pb-24">
        <div className="space-y-20">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            const isReversed = i % 2 === 1;
            return (
              <section key={feature.slug} id={feature.slug} className="scroll-mt-20" data-testid={`feature-section-${feature.slug}`}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={isReversed ? 'lg:order-2' : ''}>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${feature.color} text-white text-sm font-medium mb-4`}>
                      <Icon className="h-4 w-4" />
                      {feature.title}
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">{feature.tagline}</h2>
                    <p className="text-base text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                    <Button onClick={() => navigate('/signup')} variant="outline" className="group">
                      Try {feature.title} Free <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                  <div className={`bg-white rounded-2xl border border-gray-200 p-6 shadow-sm ${isReversed ? 'lg:order-1' : ''}`}>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">What you get</h3>
                    <ul className="space-y-3">
                      {feature.benefits.map((b, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-24 text-center bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-3">Ready to level up your game?</h2>
          <p className="text-blue-100 mb-6 max-w-lg mx-auto">Join players who are tracking matches, getting AI coaching, and improving faster with Badminton Stats.</p>
          <Button onClick={() => navigate('/signup')} className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 text-base font-semibold" data-testid="features-cta-btn">
            Create Free Account <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-gray-600 text-sm">&copy; 2026 Badminton Stats. All rights reserved.</div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link>
            <Link to="/features" className="text-gray-500 hover:text-blue-600">Features</Link>
            <Link to="/privacy" className="text-gray-500 hover:text-blue-600">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-blue-600">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
